import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Fab, Tooltip } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useLocation } from 'react-router-dom';
import { useStore } from 'src/stores';
import { signedInSubject, useLivyAccess } from './access';
import { LivyDrawer } from './LivyDrawer';
import { LivyAvatar } from './LivyAvatar';
import { LivyCommandPalette, useCommandPalette } from './LivyCommandPalette';
import { ASK_EVENT, HELP_EVENT, LivyGreeting, greetingActions, greetingSeen, markGreetingSeen, livyEvent, resumePrompt, useEngagement } from './LivyGreeting';
import { useBillingType } from './billingType';

/**
 * Mounts everything that should exist on every authenticated page: the support button that opens
 * the drawer, Cmd/Ctrl-I to toggle it, Cmd-K, and the login greeting. Staff always; customers once
 * staff switch their client on. Search is fenced server side to what each caller may read.
 */
const LivyLauncherBody = observer(function LivyLauncherBody() {
  var _store$homeDashboardS;
  const _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    open = _useState2[0],
    setOpen = _useState2[1];
  const _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    pendingAsk = _useState4[0],
    setPendingAsk = _useState4[1];
  const _useState5 = useState(null),
    _useState6 = _slicedToArray(_useState5, 2),
    pendingHelp = _useState6[0],
    setPendingHelp = _useState6[1];
  // Per login, from session storage: a second sign-in without a reload is a new greeting.
  const _useState7 = useState(0),
    _useState8 = _slicedToArray(_useState7, 2),
    rerender = _useState8[1];
  const seen = greetingSeen();
  const store = useStore();
  const enabled = useLivyAccess(store.account.userProfile.userRole);
  const _useCommandPalette = useCommandPalette(enabled),
    paletteOpen = _useCommandPalette.open,
    setPaletteOpen = _useCommandPalette.setOpen;
  const engagement = useEngagement(enabled);
  const billingType = useBillingType();
  // The dashboard store already holds every property's status for the navigation, so this costs no request.
  const inSetup = Boolean((_store$homeDashboardS = store.homeDashboardStore.getCurrentCompany) === null || _store$homeDashboardS === void 0 || (_store$homeDashboardS = _store$homeDashboardS.portfolios) === null || _store$homeDashboardS === void 0 ? void 0 : _store$homeDashboardS.some(p => p.properties.some(x => x.propertyStatus === 'Inactive')));
  const dismiss = useCallback(() => {
    markGreetingSeen();
    rerender(n => n + 1);
  }, []);
  // Closing the greeting unasked counts as a dismissed tip; two of them and tips stop early.
  const dismissTip = useCallback(() => {
    void livyEvent('tip_dismissed');
    dismiss();
  }, [dismiss]);
  const openWith = useCallback(text => {
    dismiss();
    setOpen(true);
    // An ask, even an empty one, opens on Chat; a bare open keeps the last tab.
    if (text !== undefined) setPendingAsk(text);
  }, [dismiss]);
  useEffect(() => {
    if (!enabled) return undefined;
    const onKey = e => {
      if ((e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 'i') {
        e.preventDefault();
        setOpen(v => !v);
      }
    };
    const onAsk = e => openWith(e.detail);
    const onHelp = e => {
      openWith();
      setPendingHelp(e.detail);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener(ASK_EVENT, onAsk);
    window.addEventListener(HELP_EVENT, onHelp);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener(ASK_EVENT, onAsk);
      window.removeEventListener(HELP_EVENT, onHelp);
    };
  }, [enabled, openWith]);
  if (!enabled) return null;
  const greeting = Boolean(engagement === null || engagement === void 0 ? void 0 : engagement.greet) && !seen && !open;
  // After the tip window Livy waits to be asked, unless there is setup left to finish.
  const badge = !open && !seen && (greeting || Boolean(engagement === null || engagement === void 0 ? void 0 : engagement.enabled) && !(engagement !== null && engagement !== void 0 && engagement.tips) && inSetup);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Tooltip, {
    title: "Livy (\u2318I / Ctrl+I)",
    placement: "left"
  }, /*#__PURE__*/React.createElement(Fab, {
    color: "primary",
    size: "medium",
    "aria-label": badge ? 'Ask Livy, new suggestions' : 'Ask Livy',
    onClick: () => openWith(),
    sx: {
      position: 'fixed',
      // HubSpot's chat launcher owns the bottom-right 100x96 and its iframe takes the clicks.
      right: 112,
      bottom: 24,
      zIndex: theme => theme.zIndex.drawer - 1,
      p: 0,
      overflow: 'visible'
    }
  }, /*#__PURE__*/React.createElement(Badge, {
    color: "error",
    variant: "dot",
    invisible: !badge,
    overlap: "circular",
    sx: {
      '& .MuiBadge-dot': {
        width: 12,
        height: 12,
        borderRadius: '6px',
        border: '2px solid #fff'
      }
    }
  }, /*#__PURE__*/React.createElement(LivyAvatar, {
    size: 48
  })))), greeting && /*#__PURE__*/React.createElement(LivyGreeting, {
    actions: greetingActions(billingType(), inSetup, engagement === null || engagement === void 0 ? void 0 : engagement.favorite),
    resume: resumePrompt(engagement === null || engagement === void 0 ? void 0 : engagement.usualRange),
    onAsk: openWith,
    onDismiss: dismissTip
  }), /*#__PURE__*/React.createElement(LivyDrawer, {
    open: open,
    onClose: () => setOpen(false),
    pendingAsk: pendingAsk,
    onAsked: () => setPendingAsk(null),
    pendingHelp: pendingHelp,
    onHelped: () => setPendingHelp(null)
  }), enabled && /*#__PURE__*/React.createElement(LivyCommandPalette, {
    open: paletteOpen,
    onClose: () => setPaletteOpen(false)
  }));
});
export function LivyLauncher() {
  // Logout and the next login happen without a reload, and both navigate. Re-rendering on the
  // location and keying on the session remounts every Livy surface empty for the next person, so a
  // drawer or palette left open never shows them the last person's conversation or results.
  useLocation();
  return /*#__PURE__*/React.createElement(LivyLauncherBody, {
    key: signedInSubject()
  });
}